import React, { Component, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import {
    Router,
    Route,
    IndexRoute,
    browserHistory
} from "react-router";
import _ from "underscore";
import detectBrowser from "detect-browser";
import queryString from "query-string";
import Login from "./components/LoginScreen";
import Logout from "./components/Logout";
import TimeApp from "./components/TimeInput/TimeInput";
import NotFound from "./components/404/NotFound";
import Admin from "./components/Admin";
import AuthCallback from "./components/AuthCallback";
import Welcome from "./components/Welcome/Welcome";
import ErrorNotice from "./components/ErrorNotice";
import UnsupportedBrowser from "./components/UnsupportedBrowser";
import UserUnavailable from "./components/UserUnavailable";
import { setApiTokens, onAuthorization } from "./actions/userActions";
import { clearHistoryEntries } from "./actions/historyActions";

export default function Routes({ store }) {
    const args = useMemo(() => getQueryArgs(), []);

    const requireAuth = useCallback((nextState, replace) => {
        const state = store.getState();
        const isLoggedIn = Boolean(state.apiTokens);

        if (!isLoggedIn) {
            replace("/");

            return true;
        }

    }, [store]);

    const onEnterAdmin = useCallback((nextState, replace) => {
        const state = store.getState();
        const role = state.userRole;

        if (requireAuth(nextState, replace)) {
            return;
        }

        if (!role || role.id !== "Administrator") {
            replace("/");
        }
    }, [store, requireAuth]);

    const onEnterApp = useCallback((nextState, replace) => {
        if (verifyBrowser(replace) || requireAuth(nextState, replace)) {
            return;
        }

        const state = store.getState();

        // Wait until we've loaded everything
        if (!state.userState.loaded) {
            replace("/");
        }
    }, [store, requireAuth]);

    const onEnterLogin = useCallback((nextState, replace) => {
        if (verifyBrowser(replace)) {
            return true;
        }

        const state = store.getState();

        if (state.userState.loaded){
            if (state.settings.firstTimeLogin) {
                replace("/welcome");
            } else {
                replace("/input-time");
            }
        }
    }, [store]);

    const onEnterWelcome = useCallback((nextState, replace) => {
        if (verifyBrowser(replace) || requireAuth(nextState, replace)) {
            return;
        }

        const state = store.getState();

        if (!state.userState.loaded) {
            replace("/");
        } else if (!state.settings.firstTimeLogin) {
            replace("/input-time");
        }
    }, [store, requireAuth]);

    const onEnterUserUnavailable = useCallback((nextState, replace) => {
        const state = store.getState();

        if (!state.userState.userUnavailable) {
            replace("/");
        }
    }, [store]);

    const onUpdate = useCallback(() => {
        store.dispatch(clearHistoryEntries());
    }, [store]);

    return (
        <Router history={browserHistory} onUpdate={onUpdate}>
            <Route path="/auth/mentor" component={AuthCallback} />
            <Route path="/" component={Login} onEnter={onEnterLogin} />
            <Route path="/input-time" component={TimeApp} onEnter={onEnterApp} />
            <Route path="/admin" component={Admin} onEnter={onEnterAdmin} />
            <Route path="/logout" component={Logout} />
            <Route path="/welcome" component={Welcome} onEnter={onEnterWelcome} />
            <Route path="/unsupported-browser" component={UnsupportedBrowser} />
            <Route path="/user-unavailable" component={UserUnavailable} onEnter={onEnterUserUnavailable} />
            <Route path="/error" type={args.type} errorId={args.id} component={ErrorNotice} />
            <Route path="*" component={NotFound} />
        </Router>
    );
}

Routes.propTypes = {
    store: PropTypes.shape({
        subscribe: PropTypes.func.isRequired,
        dispatch: PropTypes.func.isRequired,
        getState: PropTypes.func.isRequired
    }),
};

function getQueryArgs() {
    if (typeof window !== "undefined" && window.location && window.location.search) {
        return queryString.parse(window.location.search);
    }

    return {};
}

const supportedBrowsers = [
    "chrome",
    "firefox",
    "edge",
    "ie",
    "safari",
    "ios"
];

const minSupportedIEVersion = "11";

/**
 * @return {boolean}
 */
function isValidBrowser() {
    if (detectBrowser && _.contains(supportedBrowsers, detectBrowser.name)) {
        if (detectBrowser.name === "ie") {
            return detectBrowser.version.startsWith(`${minSupportedIEVersion}.`);
        }
        return true;
    }

    return false;
}

/**
 * @param {function} replace
 * @return {boolean} if already redirected
 */
function verifyBrowser(replace) {
    if (!isValidBrowser()) {
        replace("/unsupported-browser");

        return true;
    }
}



// WEBPACK FOOTER //
// ./app/routes.jsx